import DialogMultiActions from '../../core/DialogMultiActions';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import Errored from '../../core/Errored';
import Help from '../../core/Help';
import MenuSelector from '../../core/MenuSelector';
import PrimaryButton from '../../core/PrimaryButton';
import ProjectLanguageSelector from '../../core/ProjectLanguageSelector';
import { globalStyles as styles } from '../../core/style/gstheme';
import TitledPanel from '../../core/TitledPanel';
import TitledTextField from '../../core/TitledTextField';
import { lightSanitiseText, makeCancelable } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import { SUGGESTION_TYPE_OPTIONS } from '../constants';
import ConceptChip from '../core/ConceptChip';
import FindConcept from './FindConcept';
import SuggestionField from './SuggestionField';
import SubscriptionUsers from '../../core/SubscriptionUsers';

const loadOptions = {
    predicates: [
        "http://www.w3.org/2004/02/skos/core#inScheme",
        "http://www.w3.org/2004/02/skos/core#prefLabel",
        "http://www.w3.org/2002/07/owl#deprecated",
        "http://www.w3.org/2008/05/skos-xl#prefLabel",
        "http://www.w3.org/2004/02/skos/core#narrower",
        "http://www.w3.org/1999/02/22-rdf-syntax-ns#type"
    ]
};

class AddSuggestionDialog extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            open: false,
            text: "",
            suggestionType: props.defaultType || null,
            suggestedLabel: "",
            labelIsValid: false,
            isErrored: null,
            target: null,
            user: "All",
            inSchemeResources: [],
            parentTarget: null,
            lang: null,
            allowNotifications: GS.getActiveSubscriptionType() === "Managed" || GS.getActiveSubscriptionType() === "Trial" || GS.getActiveSubscriptionType() === "Premium"
        };
        this.cancellable = null;
    }

    async loadConcepts() {
        let resources = this.props.data.broader || [];
        if (resources.length === 0) return;
        try {
            await this.loadData(resources).promise;
            const inSchemeResources = resources.filter(resource => {
                const data = GS.data(resource, this.props.graph, false);
                if (data) {
                    const isInScheme = data.inScheme && data.inScheme.includes(this.props.schemeIRI) ? true : false;
                    return isInScheme;
                }
                return false;
            });
            let parentTarget = null;
            if (inSchemeResources.length === 1) parentTarget = inSchemeResources[0];
            this.setState({ isLoading: false, inSchemeResources, parentTarget });
        }
        catch (e) {
            if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
        }
    }

    loadData(data) {
        this.cancellable = makeCancelable(GS.loadData(data, loadOptions, this.props.graph));
        return this.cancellable;
    }

    handleClickOpen = () => {
        this.setState({
            open: true,
            lang: this.props.defaultLang
        });
    };

    addSuggestion = (notify = false) => {
        let target = this.state.target;
        let parentTarget = this.state.parentTarget;
        const user = this.state.user !== "All" ? this.state.user : null;
        this.props.onAddSuggestion(this.state.suggestionType, this.state.lang, this.state.text,
            target, parentTarget, this.state.suggestedLabel, user, notify);
        this.exitAddSuggestion();
    };

    exitAddSuggestion = () => {
        this.setState({
            open: false,
            target: null,
            parentTarget: null,
            text: "",
            user: "All",
            labelIsValid: false,
            suggestedLabel: "",
            lang: this.props.defaultLang,
            suggestionType: this.props.defaultType || null
        });
    };

    selectLanguage = (lang) => this.setState({ lang });

    handleChange = event => {
        const t = event.target;
        const cleanText = lightSanitiseText(t.value);
        this.setState({ [t.name]: cleanText });
    };

    handleTypeChange = async (suggestionType) => {
        if (suggestionType === "RemovalSuggestion" || suggestionType === "MovingSuggestion") {
            await this.loadConcepts();
            this.setState({ suggestionType, target: null });
        }
        else {
            this.setState({ suggestionType, target: null, parentTarget: null });
        }
    };

    setTarget = (target) => this.setState({ target });

    removeTarget = () => this.setState({ target: null });

    setParentTarget = (parentTarget) => this.setState({ parentTarget });

    removeParentTarget = () => this.setState({ parentTarget: null });

    labelIsValid = (suggestedLabel, isValid) => this.setState({ suggestedLabel, labelIsValid: isValid });

    handleUserChange = (user) => this.setState({ user });

    render() {

        const { classes, name, sourceConcept, graph, defaultLang, optionForXL, projectIRI,
            schemeIRI, data, isSchemeLevel, defaultType, useWorkflow } = this.props;
        const { text, lang, suggestionType, target, parentTarget, labelIsValid, user, allowNotifications,
            suggestedLabel, inSchemeResources, isErrored } = this.state;
        const selectedOption = SUGGESTION_TYPE_OPTIONS.find(option => option.value === suggestionType);
        let targetTitle = selectedOption && selectedOption["targetTitle"];
        const targetRelationship = selectedOption && selectedOption["targetRelationship"];
        const labelRequired = selectedOption && selectedOption["labelRequired"];
        if (selectedOption && !selectedOption.targetMandatory) targetTitle += " (Leave Blank for Top Concept)";

        if (isErrored) return <Errored data={isErrored} />;

        return (
            <React.Fragment>
                <PrimaryButton dataTest={"add-suggestion"} onClick={this.handleClickOpen}>Add Suggestion</PrimaryButton>
                <Dialog
                    data-test="add-suggestion-dialog"
                    maxWidth="md"
                    fullWidth
                    classes={{
                        paperWidthMd: classes.mediumDialogOverflow
                    }}
                    open={this.state.open}
                    aria-labelledby="text-dialog-title">
                    <DialogTitle data-test="add-suggestion-title" disableTypography className={classes.mainHeading} id="text-dialog-title">Add Suggestion</DialogTitle>
                    <DialogContent data-test="add-suggestion-dialog" style={{ maxHeight: '500px', overflowY: 'scroll' }}>
                        <Help textRef="ADD_SUGGESTION" />
                        <form autoComplete="off" noValidate="novalidate">
                            <div style={{ display: 'flex' }}>
                                <div style={{ flex: 1 }}>
                                    <TitledPanel dataTest="suggestion-type" height="full" title="Suggestion Type">
                                        <MenuSelector
                                            dataTest="type-selector"
                                            selected={defaultType}
                                            handleChange={this.handleTypeChange}
                                            hintWord="suggestion type"
                                            options={SUGGESTION_TYPE_OPTIONS
                                                .filter(suggestionType => isSchemeLevel ? suggestionType.schemeLevel : true)
                                                .filter(suggestionType => {
                                                    if (suggestionType.hasOwnProperty("requiredRelationship")
                                                        && (!data[suggestionType.requiredRelationship]
                                                            || data[suggestionType.requiredRelationship].length === 0)) return false;
                                                    if (suggestionType.active) return true;
                                                    return false;
                                                })} />
                                    </TitledPanel>
                                </div>
                                {allowNotifications &&
                                    <div style={{ marginLeft: 12, width: 200 }}>
                                        <TitledPanel dataTest="assignee" height="full" title="Assign To">
                                            <SubscriptionUsers
                                                projectIRI={projectIRI}
                                                allLabel="Unassigned"
                                                includeLabel={false}
                                                onUserChange={this.handleUserChange} />
                                        </TitledPanel>
                                    </div>
                                }
                            </div>
                            {labelRequired &&
                                <SuggestionField
                                    isValid={this.labelIsValid}
                                    schemeIRI={schemeIRI}
                                    optionForXL={optionForXL}
                                    graph={graph}
                                    defaultLang={defaultLang} />
                            }
                            {selectedOption && selectedOption["parentTitle"] && inSchemeResources.length > 0 &&
                                <TitledPanel
                                    dataTest="select-parent-target"
                                    overflow={true}
                                    margin={true}
                                    title={selectedOption["parentTitle"]}>
                                    {parentTarget ?
                                        <div>
                                            <ConceptChip
                                                id={parentTarget}
                                                graph={graph}
                                                onRemove={this.removeParentTarget}
                                                loadOptions={{
                                                    language: defaultLang
                                                }}
                                                defaultLang={defaultLang} />
                                        </div>
                                        :
                                        <div data-test="broader-selection" style={{ maxHeight: 80, overflowY: 'scroll' }}>
                                            {inSchemeResources.map(parentTargetConceptOption =>
                                                <ConceptChip
                                                    key={parentTargetConceptOption}
                                                    id={parentTargetConceptOption}

                                                    graph={graph}
                                                    loadOptions={{
                                                        language: defaultLang
                                                    }}
                                                    onSelect={() => this.setParentTarget(parentTargetConceptOption)}
                                                    defaultLang={defaultLang} />
                                            )}
                                        </div>
                                    }
                                </TitledPanel>
                            }
                            {selectedOption && selectedOption["targetTitle"] &&
                                <TitledPanel overflow={true} margin={true} title={targetTitle}>
                                    {target ?
                                        <ConceptChip
                                            id={target}
                                            graph={graph}
                                            onRemove={this.removeTarget}
                                            loadOptions={{
                                                language: defaultLang
                                            }}
                                            defaultLang={defaultLang} />
                                        :
                                        targetRelationship ?
                                            <div style={{ maxHeight: 80, overflow: 'scroll' }}>
                                                {data[targetRelationship].map(targetConceptOption =>
                                                    <ConceptChip
                                                        key={targetConceptOption}
                                                        id={targetConceptOption}
                                                        graph={graph}
                                                        loadOptions={{
                                                            language: defaultLang
                                                        }}
                                                        onSelect={() => this.setTarget(targetConceptOption)}
                                                        defaultLang={defaultLang} />
                                                )}
                                            </div>
                                            :
                                            <div style={{ height: 43, overflow: 'visible' }}>
                                                <FindConcept
                                                    source={sourceConcept}
                                                    schemeIRI={schemeIRI}
                                                    graph={graph}
                                                    useWorkflow={useWorkflow}
                                                    exclude={[sourceConcept]}
                                                    onSelectConcept={this.setTarget} />
                                            </div>
                                    }
                                </TitledPanel>
                            }
                            <TitledPanel dataTest="detail-language" title="Language" margin={true}>
                                <ProjectLanguageSelector selectedLang={lang} includeLabel={false} onSelect={this.selectLanguage} />
                            </TitledPanel>
                            <TitledTextField
                                dataTest="language-text"
                                disabled={!suggestionType}
                                name="text"
                                title={name || "Detail"}
                                margin={true}
                                placeholder="Enter detail about your suggestion"
                                required={true}
                                text={text}
                                multiline={true}
                                rows={3}
                                maxLength={500}
                                onEnter={text.length > 0 && lang !== "" ? this.addItem : null}
                                onChange={this.handleChange} />
                        </form>
                    </DialogContent>
                    <DialogMultiActions onCancel={this.exitAddSuggestion}>
                        <PrimaryButton
                            dataTest="add-suggestion-button"
                            disabled={labelIsValid || lang === "" || text.length === 0 ||
                                (labelRequired && suggestedLabel === "") ||
                                (selectedOption.parentTitle && !parentTarget && data["broader"] && data["broader"].length > 0) ||
                                !suggestionType || (selectedOption && selectedOption.targetMandatory && !target)}
                            onClick={() => this.addSuggestion()}>Add</PrimaryButton>
                        {user !== "All" &&
                            <PrimaryButton
                                dataTest="add-notify-suggestion-button"
                                disabled={labelIsValid || lang === "" || text.length === 0 ||
                                    (labelRequired && suggestedLabel === "") ||
                                    (selectedOption.parentTitle && !parentTarget && data["broader"] && data["broader"].length > 0) ||
                                    !suggestionType || (selectedOption && selectedOption.targetMandatory && !target)}
                                onClick={() => this.addSuggestion(true)}>Add and Notify</PrimaryButton>
                        }
                    </DialogMultiActions>
                </Dialog>
            </React.Fragment>
        );
    }
}

AddSuggestionDialog.defaultProps = {
    useWorkflow: false
};

AddSuggestionDialog.propTypes = {
    defaultType: PropTypes.string,
    isSchemeLevel: PropTypes.bool.isRequired,
    data: PropTypes.object.isRequired,
    schemeIRI: PropTypes.string.isRequired,
    useWorkflow: PropTypes.bool,
    projectIRI: PropTypes.string.isRequired,
    graph: PropTypes.string.isRequired,
    sourceConcept: PropTypes.string.isRequired,
    defaultLang: PropTypes.string.isRequired,
    optionForXL: PropTypes.bool.isRequired,
    onAddSuggestion: PropTypes.func.isRequired,
    classes: PropTypes.object
};

export default withStyles(styles)(AddSuggestionDialog);